
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, ArrowRight, Building2, Home, CheckCircle2 } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { townData, getHighPriorityTowns, getMediumPriorityTowns, getCommercialFocusTowns } from '@/data/townData';

const LocationsPage = () => {
  const highPriorityTowns = getHighPriorityTowns();
  const mediumPriorityTowns = getMediumPriorityTowns();
  const standardPriorityTowns = townData.filter(t => t.priority === 'standard');
  const commercialTowns = getCommercialFocusTowns();

  // Group by county
  const fairfieldCountyTowns = townData.filter(t => t.county === 'Fairfield');
  const newHavenCountyTowns = townData.filter(t => t.county === 'New Haven');

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LandscapingBusiness",
    "name": "PK Landscape Associates",
    "description": "Professional landscaping services in Fairfield County and New Haven County, Connecticut. Serving residential and commercial properties.",
    "url": "https://pklandscapect.com/locations",
    "telephone": "{{PHONE}}",
    "areaServed": townData.map(town => ({
      "@type": "City",
      "name": town.name,
      "containedInPlace": {
        "@type": "State",
        "name": "Connecticut"
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Service Areas | Landscaping in Fairfield & New Haven County CT</title>
        <meta 
          name="description" 
          content="PK Landscape Associates serves 22+ towns across Fairfield County and New Haven County, CT. Professional landscaping, lawn care, and hardscaping for residential and commercial properties." 
        />
        <link rel="canonical" href="https://pklandscapect.com/locations" />
        <meta property="og:title" content="Service Areas | PK Landscape Associates" />
        <meta property="og:description" content="Professional landscaping services across Fairfield County and New Haven County, Connecticut." />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-pkgreen-900 via-pkgreen-800 to-pkgreen-700">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block bg-pkgreen-600/50 text-pkgreen-100 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              Greater Bridgeport & Fairfield County
            </span>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-6">
              Landscaping Service Areas in Connecticut
            </h1>
            
            <p className="text-xl text-pkgreen-100 mb-8 max-w-3xl mx-auto">
              PK Landscape Associates proudly serves over 22 towns across Fairfield County and New Haven County. 
              From luxury estates in Westport to commercial properties in Bridgeport, we deliver excellence throughout the region.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-pkgreen-700/50 px-4 py-2 rounded-full">
                <Home className="h-5 w-5 text-pkgreen-300" />
                <span className="text-white">Residential</span>
              </div>
              <div className="flex items-center gap-2 bg-pkgreen-700/50 px-4 py-2 rounded-full">
                <Building2 className="h-5 w-5 text-pkgreen-300" />
                <span className="text-white">Commercial</span>
              </div>
              <div className="flex items-center gap-2 bg-pkgreen-700/50 px-4 py-2 rounded-full">
                <CheckCircle2 className="h-5 w-5 text-pkgreen-300" />
                <span className="text-white">HOA & Property Management</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Towns - High Priority */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-pkstone-900 mb-4">
              Featured Service Areas
            </h2>
            <p className="text-pkstone-600 max-w-2xl mx-auto">
              Our most active service areas in Fairfield County's premier communities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {highPriorityTowns.map((town) => (
              <Link
                key={town.slug}
                to={`/locations/${town.slug}`}
                className="group bg-pkstone-50 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                <div className="bg-pkgreen-700 p-6 group-hover:bg-pkgreen-600 transition-colors">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-2xl font-serif font-bold text-white mb-1">
                        {town.name}, CT
                      </h3>
                      <span className="text-pkgreen-200 text-sm">{town.county} County</span>
                    </div>
                    <MapPin className="h-8 w-8 text-pkgreen-300" />
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-pkstone-600 text-sm mb-4 line-clamp-2">
                    {town.heroText.substring(0, 120)}...
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {town.serviceHighlights.slice(0, 2).map((highlight, i) => (
                      <span key={i} className="bg-pkgreen-100 text-pkgreen-800 text-xs px-2 py-1 rounded-full">
                        {highlight}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center text-pkgreen-600 font-medium group-hover:text-pkgreen-700">
                    View {town.name} Services
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Fairfield County Towns */}
      <section className="py-16 bg-pkstone-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-serif font-bold text-pkstone-900 mb-8 text-center">
            Fairfield County Service Areas
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {fairfieldCountyTowns.map((town) => (
              <Link
                key={town.slug}
                to={`/locations/${town.slug}`}
                className="bg-white p-4 rounded-lg hover:shadow-md transition-all group flex items-center gap-3"
              >
                <MapPin className="h-5 w-5 text-pkgreen-600 flex-shrink-0" />
                <div>
                  <span className="text-pkstone-800 font-medium group-hover:text-pkgreen-700 block">
                    {town.name}
                  </span>
                  <span className="text-pkstone-500 text-xs">
                    {town.commercialFocus ? 'Residential & Commercial' : 'Residential'}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* New Haven County Towns */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-serif font-bold text-pkstone-900 mb-8 text-center">
            New Haven County Service Areas
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {newHavenCountyTowns.map((town) => (
              <Link
                key={town.slug}
                to={`/locations/${town.slug}`}
                className="bg-pkstone-50 p-4 rounded-lg hover:shadow-md transition-all group flex items-center gap-3"
              >
                <MapPin className="h-5 w-5 text-pkgreen-600 flex-shrink-0" />
                <div>
                  <span className="text-pkstone-800 font-medium group-hover:text-pkgreen-700 block">
                    {town.name}
                  </span>
                  <span className="text-pkstone-500 text-xs">
                    {town.commercialFocus ? 'Residential & Commercial' : 'Residential'}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Commercial Focus Section */}
      <section className="py-16 bg-pkgreen-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-white mb-4">
              Commercial Landscaping Services
            </h2>
            <p className="text-pkgreen-100 max-w-2xl mx-auto">
              We maintain commercial properties, HOA communities, and business campuses across these key markets.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {commercialTowns.map((town) => (
              <Link
                key={town.slug}
                to={`/locations/${town.slug}`}
                className="bg-pkgreen-700/50 hover:bg-pkgreen-700 p-4 rounded-lg text-center transition-colors group"
              >
                <Building2 className="h-6 w-6 text-pkgreen-300 mx-auto mb-2" />
                <span className="text-white font-medium text-sm group-hover:text-pkgreen-100">
                  {town.name}
                </span>
              </Link>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/commercial"
              className="bg-white text-pkgreen-800 hover:bg-pkgreen-50 py-3 px-8 rounded-md inline-flex items-center font-medium transition-all duration-300"
            >
              Learn About Commercial Services
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-pkstone-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-serif font-bold text-pkstone-900 mb-4">
            Don't See Your Town Listed?
          </h2>
          <p className="text-pkstone-600 mb-8 max-w-2xl mx-auto">
            We may still be able to serve your area. Contact us to discuss your property's landscaping needs.
          </p>
          <Link
            to="/contact"
            className="bg-pkgreen-600 hover:bg-pkgreen-700 text-white py-3 px-8 rounded-md inline-flex items-center font-medium transition-all duration-300"
          >
            Contact Us
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default LocationsPage;
